import { z } from 'zod/v4';

export const AdminProductionProcessesListSortColumns = [
  'name',
  'description',
  'updatedAt',
] as const;

export type AdminProductionProcessesListSortColumn =
  (typeof AdminProductionProcessesListSortColumns)[number];

const sortColumns = AdminProductionProcessesListSortColumns.join('|');

// One or more comma-separated `<column>:<ASC|DESC>` terms, e.g.
// `name:DESC,updatedAt:ASC` — the same grammar the legacy route accepted.
const sortPattern = new RegExp(
  `^(${sortColumns}):(ASC|DESC)(,(${sortColumns}):(ASC|DESC))*$`,
);

// Querystrings arrive as strings; accept a real boolean too.
const booleanLike = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform(v => v === true || v === 'true');

/**
 * The legacy route also read a `q` param, but its querystring schema stripped
 * it before the handler saw it; it is not carried over.
 */
export const AdminProductionProcessesListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: z.string().regex(sortPattern).optional(),
  // JSON object; only `name` is honoured, as a case-insensitive substring match.
  filter: z.string().max(512).optional(),
  // Every matching row in one page, ignoring `page` / `count` — the pickers.
  isDropDown: booleanLike,
});

export type AdminProductionProcessesListQuery = z.infer<
  typeof AdminProductionProcessesListQuerySchema
>;

/**
 * One row of the admin table and of the pickers. Legacy's dropdown branch
 * returned fewer columns; both modes now return the same row.
 */
export const AdminProductionProcessRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  color: z.string().nullable(),
  iconColor: z.string().nullable(),
  depthLevel: z.number().nullable(),
  createdAt: z.coerce.date().nullable(),
  createdByUserId: z.string().nullable(),
  updatedAt: z.coerce.date().nullable(),
  updatedByUserId: z.string().nullable(),
  updatedByUserName: z.string().nullable(),
});

export type AdminProductionProcessRow = z.infer<
  typeof AdminProductionProcessRowSchema
>;

/** The `data` payload; the route wraps it with the paginated `metadata` block. */
export const AdminProductionProcessesListResponseSchema = z.array(
  AdminProductionProcessRowSchema,
);

export type AdminProductionProcessesListResponse = z.infer<
  typeof AdminProductionProcessesListResponseSchema
>;
